import imageCompression from 'browser-image-compression';
import { inferContentType, isAllowedContentType } from 'services/directUploadService';

export const blobToBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise<string | ArrayBuffer | null>((resolve) => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

// Recomendação do backend para fotos enviadas por direct upload:
// ~1600px no maior lado, qualidade 80. Formatos fora da lista aceita
// (gif, bmp, heic...) são convertidos para JPEG na compressão.
export const IMAGE_MAX_DIMENSION = 1600;
export const IMAGE_QUALITY = 0.8;
export const IMAGE_MAX_SIZE_MB = 1;

function outputTypeFor(contentType: string): string {
  return isAllowedContentType(contentType) ? contentType : 'image/jpeg';
}

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

function renameForType(name: string, contentType: string): string {
  const extension = EXTENSION_BY_TYPE[contentType];
  if (!extension) return name;
  const base = name.replace(/\.[^.]+$/, '') || 'imagem';
  return `${base}.${extension}`;
}

export async function compressImage(
  file: File,
  setPercentage?: (progress: number) => void,
): Promise<File> {
  const contentType = outputTypeFor(inferContentType(file));
  const options = {
    maxSizeMB: IMAGE_MAX_SIZE_MB,
    maxWidthOrHeight: IMAGE_MAX_DIMENSION,
    initialQuality: IMAGE_QUALITY,
    fileType: contentType,
    onProgress: (progress: number) => setPercentage && setPercentage(progress),
  };

  const compressed: Blob = await imageCompression(file, options);
  const name = renameForType(file.name, contentType);

  return new File([compressed], name, {
    type: contentType,
    lastModified: file.lastModified,
  });
}

/**
 * Deixa o arquivo pronto para o direct upload: imagens são comprimidas
 * (e convertidas para JPEG quando o formato não é aceito pela API); PDF
 * segue como está, apenas garantindo o content type.
 */
export async function prepareFileForUpload(
  file: File,
  setPercentage?: (progress: number) => void,
): Promise<File> {
  const contentType = inferContentType(file);

  if (contentType === 'application/pdf') {
    return file.type === contentType
      ? file
      : new File([file], file.name, { type: contentType, lastModified: file.lastModified });
  }

  if (contentType.startsWith('image/')) {
    return compressImage(file, setPercentage);
  }

  throw new Error(`Unsupported content type: ${contentType || 'unknown'}`);
}

export async function getCompressImageBase64(
  file: File,
  setPercentage?: (progress: number) => void,
): Promise<string> {
  const compressedFile = await compressImage(file, setPercentage);
  const fileBase64 = await blobToBase64(compressedFile);

  return fileBase64?.toString() || '';
}
